import React from 'react';
import { tv } from 'tailwind-variants';
import type {
  FormInstance,
  StepConfig,
  FormValues,
  FormErrors,
  FormTouched,
  FieldConfig,
  FieldGroup as FieldGroupType,
} from '../../types';
import { InlineCard } from './InlineCard';
import { FieldGroup } from '../WillsFormStep/FieldGroup';
import ButtonWrapper from 'helpers/SitecoreWrappers/ButtonWrapper/ButtonWrapper';
import { useUIText } from '../../hooks/useUIText';

interface InlineCardListProps {
  instances: FormInstance[];
  steps: StepConfig[];
  title: string;
  onEdit: (instanceId: string) => void;
  editingInstanceId?: string | null; // Add this
  fieldGroup?: FieldGroupType; // Add fieldGroup for form rendering
  values?: FormValues; // Add form values
  errors?: FormErrors; // Add errors
  touched?: FormTouched; // Add touched
  handleChange?: (field: FieldConfig, rawValue: unknown) => void; // Add handleChange
  setTouched?: React.Dispatch<React.SetStateAction<FormTouched>>;
  onUpdate?: () => void;
  isSubmitting?: boolean;
  canAddMore?: boolean;
  displayTitleFields?: string[];
  displayFields?: string[];
  editableFields?: string[];
  editFields?: FieldConfig[];
  showEdit?: boolean;
}

export const InlineCardList: React.FC<InlineCardListProps> = ({
  instances,
  steps,
  title,
  onEdit,
  editingInstanceId,
  fieldGroup,
  values,
  errors,
  touched,
  handleChange,
  setTouched,
  onUpdate,
  isSubmitting,
  canAddMore,
  displayTitleFields,
  displayFields,
  editableFields,
  editFields,
  showEdit = true,
}) => {
  const { getButtonText } = useUIText();

  if (instances.length === 0) {
    return null;
  }
  // Create filtered fieldGroup for editing (only editableFields + editFields)
  const getEditableFieldGroup = (): FieldGroupType | undefined => {
    if (!fieldGroup) return undefined;

    let fieldsToShow: FieldConfig[] = [];

    if (editableFields && editableFields.length > 0) {
      // Only include fields that are in editableFields
      fieldsToShow = fieldGroup.fields.filter((field) => editableFields.includes(field.id));
    } else {
      // If no editableFields specified, show all fields
      fieldsToShow = [...fieldGroup.fields];
    }

    // Add editFields (new fields for editing)
    if (editFields && editFields.length > 0) {
      fieldsToShow = [...fieldsToShow, ...editFields];
    }

    return {
      ...fieldGroup,
      fields: fieldsToShow,
    };
  };

  const editableFieldGroup = getEditableFieldGroup();

  const { container, editFormContainer, saveButtonContainer } = TAILWIND_VARIANTS();

  return (
    <div className={container()}>
      {instances.map((instance, instanceIndex) => {
        // If this instance is being edited, render form instead of card
        if (
          editingInstanceId === instance.instanceId &&
          fieldGroup &&
          values &&
          errors &&
          touched &&
          handleChange &&
          setTouched
        ) {
          return (
            <div key={instance.instanceId} className={editFormContainer()}>
              <FieldGroup
                fieldGroup={editableFieldGroup || fieldGroup}
                values={values}
                errors={errors}
                touched={touched}
                handleChange={handleChange}
                setTouched={setTouched}
              />
              {/* Update button - appears immediately after form when editing */}
              {onUpdate && (canAddMore || editingInstanceId === instance.instanceId) && (
                <div className={saveButtonContainer()}>
                  <ButtonWrapper
                    type="button"
                    text={getButtonText('save', 'Save')}
                    ctaComponentClass="default"
                    ctaVariant="primary"
                    onClick={onUpdate}
                    disabled={isSubmitting}
                    className="w-fit leading-[100%] md:leading-[100%] lg:leading-[100%] h-12"
                  />
                </div>
              )}
            </div>
          );
        }

        // Otherwise, render the card normally
        return (
          <InlineCard
            key={instance.instanceId}
            instance={instance}
            instanceIndex={instanceIndex}
            steps={steps}
            title={title}
            onEdit={showEdit ? onEdit : () => {}}
            displayTitleFields={displayTitleFields}
            displayFields={displayFields}
          />
        );
      })}
    </div>
  );
};

const TAILWIND_VARIANTS = tv({
  slots: {
    container: ['w-full', 'flex', 'flex-col', 'justify-start', 'gap-spacing-spacing-24'],
    editFormContainer: ['w-full', 'flex', 'flex-col', 'justify-start', 'gap-spacing-spacing-24'],
    saveButtonContainer: ['w-fit'],
  },
});
